import {
  ORDER_INQUIRY_ANSWER_MAX,
  ORDER_INQUIRY_RESOLUTION_LABELS,
  ORDER_INQUIRY_RESOLUTIONS,
  type OrderInquiryResolution,
} from '@repo/api-contracts';
import { type FormEvent, type ReactElement, useState } from 'react';
import { Link } from 'react-router';
import { Button, formStyles } from '@/shared/ui';
import { useAnswerOrderInquiry } from '../model/use-answer-order-inquiry';

interface AnswerOrderInquiryFormProps {
  readonly inquiry: {
    readonly id: string;
    readonly orderId: string;
    readonly updatedAt: string;
  };
}

const CONFLICT_MESSAGE = '문의가 바뀌었습니다. 새로 불러온 뒤 다시 시도해 주세요.';

const isResolution = (value: string): value is OrderInquiryResolution =>
  ORDER_INQUIRY_RESOLUTIONS.some((resolution) => resolution === value);

/** 문의 답변 폼 — 답변 글과 처리 결과를 보낸다. 환불은 여기서 처리하지 않고 주문 상세의 결제 정보로 안내한다. */
export const AnswerOrderInquiryForm = ({ inquiry }: AnswerOrderInquiryFormProps): ReactElement => {
  const [answerText, setAnswerText] = useState('');
  const [resolution, setResolution] = useState<OrderInquiryResolution | ''>('');
  const { answer, isPending, error, isConflict } = useAnswerOrderInquiry();
  const trimmed = answerText.trim();
  const isRefund = resolution === 'PARTIAL_REFUND' || resolution === 'FULL_REFUND';

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (trimmed.length === 0 || resolution === '' || isPending) return;
    await answer({
      inquiryId: inquiry.id,
      answer: trimmed,
      resolution,
      expectedUpdatedAt: inquiry.updatedAt,
    });
  };

  return (
    <form
      onSubmit={(event) => void submit(event)}
      className="flex flex-col gap-3 rounded-2xl border border-foreground/[0.06] bg-card px-5 py-4"
    >
      <h2 className="font-medium">답변하기</h2>
      <textarea
        aria-label="답변"
        className={formStyles.textarea}
        value={answerText}
        maxLength={ORDER_INQUIRY_ANSWER_MAX}
        onChange={(event) => setAnswerText(event.target.value)}
      />
      <p className="text-right text-xs text-muted-foreground">
        {answerText.length} / {ORDER_INQUIRY_ANSWER_MAX}
      </p>
      <select
        aria-label="처리 결과"
        className={formStyles.select}
        value={resolution}
        onChange={(event) =>
          setResolution(isResolution(event.target.value) ? event.target.value : '')
        }
      >
        <option value="">처리 결과 선택</option>
        {ORDER_INQUIRY_RESOLUTIONS.map((value) => (
          <option key={value} value={value}>
            {ORDER_INQUIRY_RESOLUTION_LABELS[value]}
          </option>
        ))}
      </select>
      {isRefund ? (
        <div className={formStyles.notice}>
          <p>환불은 주문 상세의 결제 정보에서 진행하세요</p>
          <Link to={`/orders/${inquiry.orderId}`} className="underline underline-offset-4">
            주문 상세에서 환불하기
          </Link>
        </div>
      ) : null}
      {isConflict ? (
        <p role="alert" className="text-sm text-destructive">
          {CONFLICT_MESSAGE}
        </p>
      ) : error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
      <div className="flex justify-end">
        <Button
          type="submit"
          size="sm"
          isLoading={isPending}
          disabled={trimmed.length === 0 || resolution === '' || isPending}
        >
          답변 보내기
        </Button>
      </div>
    </form>
  );
};
